import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { ResultsDisclaimer } from "@/components/site/ResultsDisclaimer";

const URL = "https://joinkcn.com/value";

const COVERED = [
  {
    title: "Marketing and content",
    body: "Brand systems, listing marketing, social content, and campaign production handled by our team so you are not paying a freelancer or doing it at 11pm.",
    to: "/marketing-content-and-strategy" as const,
  },
  {
    title: "Technology and AI",
    body: "CRM, database automation, AI follow-up, transaction tech, and the integrations that hold it together. One stack, already configured.",
    to: "/technology-and-tools" as const,
  },
  {
    title: "Leads and referrals",
    body: "Referral flow, relocation, and network-sourced business you cannot buy on the open market. Warm introductions, not cold clicks.",
    to: "/sales-leads-and-referrals" as const,
  },
  {
    title: "Coaching and training",
    body: "Weekly accountability, sales training, and direct access to leaders who have built at scale. Not a recording library.",
    to: "/coaching-and-training" as const,
  },
  {
    title: "Admin, ISA, and transaction support",
    body: "Contract-to-close coordination and inside sales support so your hours go to the work only you can do.",
    to: "/growth-and-team-building" as const,
  },
  {
    title: "Growth and team building",
    body: "The path from producer to owner: hiring, structure, leadership, and equity through the KCN model.",
    to: "/growth-and-team-building" as const,
  },
];

const FAQS = [
  {
    q: "What does the Kristan Cole Network cover for its agents?",
    a: "KCN covers marketing and content production, CRM and AI technology, lead and referral flow, coaching and training, and admin, ISA, and transaction coordination support. These are the same line items most agents pay for out of pocket, typically $30,000 to $80,000 a year at scale.",
  },
  {
    q: "How is that different from a standard brokerage split?",
    a: "A standard split buys you a license to hang and a desk. The KCN split funds an operating system: people, technology, and marketing that keep producing whether or not you are working that week.",
  },
  {
    q: "Do I keep my brand?",
    a: "Yes. You keep your name and your client relationships. KCN supplies the infrastructure behind them.",
  },
  {
    q: "Is there a production requirement?",
    a: "There is no hard number, but the model is built for agents who are already producing and are capped out by their own capacity rather than by their market.",
  },
];

export const Route = createFileRoute("/value")({
  head: () => ({
    meta: [
      { title: "Keller Williams Commission Split - What KCN Covers" },
      {
        name: "description",
        content:
          "How the Keller Williams commission split, the KCN real estate team commission split, and Keller Williams profit share work, plus the costs KCN covers for you.",
      },

      { property: "og:type", content: "website" },
      { property: "og:title", content: "The Value of Joining KCN" },
      {
        property: "og:description",
        content:
          "The services KCN covers, and what they would cost you to build alone.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://joinkcn.com/" },
            { "@type": "ListItem", position: 2, name: "Value", item: URL },
          ],
        }),
      },
    ],
  }),
  component: ValuePage,
});

function ValuePage() {
  return (
    <SiteLayout>
      <section className="pt-36 pb-14 border-b border-border">
        <div className="container-edge">
          <p className="eyebrow mb-6">The Value</p>
          <h1 className="headline-xl max-w-5xl">
            You are already paying for
            <br />
            <span className="serif-italic text-primary">all of this.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-foreground/80 leading-relaxed">
            Marketing. Technology. Coaching. Admin. Every top producer funds the
            same infrastructure. Most agents pay for it piece by piece, out of
            pocket, and still end up doing half of it themselves. At KCN it is
            already built, already staffed, and already running.
          </p>

        </div>
      </section>

      {/* AEO quotable answer */}
      <Section tone="darker" className="py-14 md:py-16">
        <div className="max-w-3xl border-l-2 border-primary pl-6">
          <p className="eyebrow mb-4">In short</p>
          <p className="text-lg md:text-xl text-foreground/90 leading-relaxed">
            The Kristan Cole Network covers marketing and content, CRM and AI
            technology, lead and referral flow, coaching and training, and admin,
            ISA, and transaction support. For a producing agent, replicating that
            stack alone typically runs $30,000 to $80,000 a year plus the hours to
            manage it.
          </p>
          <ResultsDisclaimer />
        </div>
      </Section>

      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-7">
            <SectionEyebrow>What KCN covers</SectionEyebrow>
            <h2 className="headline-lg">Six line items off your P&amp;L.</h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-foreground/75 leading-relaxed">
              Not discounts. Not a vendor list. Built, staffed, and run by the
              network on your behalf.
            </p>
          </div>
        </div>

        <div className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-3">
          {COVERED.map((c, i) => (
            <Link
              key={c.title}
              to={c.to}
              className="group bg-background p-8 transition-colors hover:bg-surface"
            >
              <div className="eyebrow mb-6">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-xl md:text-2xl mb-4 group-hover:text-primary transition-colors">
                {c.title}
              </h3>
              <p className="text-foreground/75 leading-relaxed text-sm">{c.body}</p>
            </Link>
          ))}
        </div>
      </Section>

      <Section tone="cream">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>Why I do what I do</SectionEyebrow>
            <h2 className="headline-lg">
              I know what it costs
              <br />
              <span className="serif-italic text-primary">to build this alone.</span>
            </h2>
            <p className="mt-6 text-sm uppercase tracking-[0.22em] text-cream-foreground/60">
              Kristan Cole
            </p>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-cream-foreground/85">
            <p>
              Forty years in this business, and I still remember the start: a
              single mom raising five children in Alaska, no safety net, no
              luxury background. Just grit and a refusal to accept the life I
              was handed. That is how I became an agent, then a broker, then an
              owner of multiple market centers.
            </p>
            <p>
              Last year, 92% of agents in the country closed fewer than four
              transactions. That number is the reason this network exists. I
              want agents earning $100,000, then $250,000, consistently, with a
              life that holds up after the closings stop.
            </p>
            <p>
              So I built the team I wish I'd had. Leadership talent from the US,
              Canada, Australia, and the Philippines. The systems, the coaching,
              the technology, the support. Don Aldrich, David Morse, Desiree
              Basua, and many others didn't just make more money here. They
              changed what their families get to do next.
            </p>
            <p>
              Real estate is only the vehicle. My fulfillment is making sure no
              one has to start the way I started.
            </p>
            <p>
              <Link to="/the-cole-method" className="text-primary hover:underline">
                See how the COLE Method works
              </Link>
              .
            </p>
            <ResultsDisclaimer />
          </div>
        </div>
      </Section>

      <Section tone="darker">
        <div className="max-w-3xl">
          <SectionEyebrow>Does this describe you?</SectionEyebrow>
          <h2 className="headline-lg mb-8">
            You have the grit.
            <br />
            <span className="serif-italic text-primary">You need the road map.</span>
          </h2>
          <ul className="space-y-3 text-lg text-foreground/85 border-t border-border/60 pt-6">
            {[
              "You are producing, but you do not have the support to reach your full potential.",
              "You want to clear $100,000 a year and never look back.",
              "You want to own your home, hold investment property, and retire on more than social security.",
              "You want to give your family the future you know they deserve.",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="mt-2.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <ResultsDisclaimer />
        </div>
      </Section>


      <Section tone="dark">
        <div className="max-w-3xl">
          <SectionEyebrow>Questions</SectionEyebrow>
          <h2 className="headline-md mb-10">Straight answers.</h2>
          <dl className="divide-y divide-border border-y border-border">
            {FAQS.map((f) => (
              <div key={f.q} className="py-7">
                <dt className="font-display text-lg md:text-xl mb-3">{f.q}</dt>
                <dd className="text-foreground/75 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Section>

      <CTABanner
        cta="roundtable"
        eyebrow="Invitation Only"
        title="See how we operate before you decide anything."
        body="The best way to understand the value is to sit in the room. Request an invite to the Monthly Market Round Table and watch how this team works."
      />

    </SiteLayout>
  );
}
